import { forwardRef, useEffect, useRef, useState } from 'react';
import { createSubmission, uploadPhoto, validatePhoto } from '../lib/api';
import { compressPhoto, formatBytes } from '../lib/photo';
import { safeLocalStorage, storedRef } from '../lib/ref';
import {
  COUNTRIES,
  DC_WARDS,
  DEFAULT_COUNTRY,
  DEFAULT_STATE,
  MAX_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTE_LENGTH,
  MAX_PHOTO_BYTES,
  US_STATES,
} from '../shared/types';

type Phase = 'idle' | 'compressing' | 'uploading' | 'submitting' | 'done';

/** What a shrunk photo went from, so the note can show the before/after. */
interface Shrunk {
  from: number;
  to: number;
}

const SHARE_TEXT = "I deleted DoorDash. Three demands, and here's how to close your account:";

/**
 * Read at render time (not module scope) so tests can stub
 * `window.location.origin` and so this is correct in dev, test, and prod —
 * including the day the custom domain goes live — with no follow-up edit.
 */
function shareLinks(): { bluesky: string; twitter: string } {
  const origin = window.location.origin;
  return {
    bluesky: `https://bsky.app/intent/compose?text=${encodeURIComponent(`${SHARE_TEXT} ${origin}`)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(origin)}`,
  };
}

export const SubmitForm = forwardRef<HTMLElement>(function SubmitForm(_props, ref) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [state, setState] = useState(DEFAULT_STATE);
  const [ward, setWard] = useState('');
  // Consent to appear on the public wall. Defaults to true (opt-out, confirmed
  // at the sprint gate): the box is checked on load, and unchecking it keeps the
  // submission private. Sent as `public` to the API, which stores it explicitly.
  const [isPublic, setIsPublic] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [shrunk, setShrunk] = useState<Shrunk | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  // React does not control a file input's value, so `file` going null is not
  // enough to unpick it — see clearPhoto().
  const photoInput = useRef<HTMLInputElement>(null);

  // Compression is async, so two picks in quick succession (the input is
  // disabled while compressing, but that only guards the common case — e.g. a
  // drop handler or a double-fired change event can still race it) can settle
  // out of order. Without this, whichever compression finishes LAST wins,
  // even if it was picked FIRST — silently reviving a photo the user already
  // replaced. Each call captures the token current at its start and bails if
  // a newer pick has since started.
  const pickToken = useRef(0);

  // Object URLs leak until revoked; tie the lifetime to the selected file.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const busy = phase === 'compressing' || phase === 'uploading' || phase === 'submitting';

  /**
   * Compress at PICK time, not at submit time: validation runs here, so an
   * 11MB phone photo would otherwise be rejected before the user ever gets to
   * press the button. Whatever survives this is the exact File that gets
   * described to /upload-url and PUT to S3 — those must not diverge.
   */
  async function onPickFile(picked: File | null) {
    const token = ++pickToken.current;
    setError(null);
    setShrunk(null);
    if (!picked) {
      setFile(null);
      return;
    }

    setPhase('compressing');
    // compressPhoto never throws: on failure (HEIC outside Safari, mainly) it
    // hands back the original and the size check below decides.
    const { file: candidate, compressed, originalSize } = await compressPhoto(picked);

    // A newer pick started while this one was compressing — let its own
    // resolution decide the outcome instead of clobbering it with a stale one.
    if (pickToken.current !== token) return;

    setPhase('idle');

    const invalid = validatePhoto(candidate);
    if (invalid) {
      setError(invalid);
      setFile(null);
      // Same reason as clearPhoto(): leave the rejected file sitting in the
      // input and re-picking it fires no change event at all.
      resetPhotoInput();
      return;
    }
    setFile(candidate);
    if (compressed) setShrunk({ from: originalSize, to: candidate.size });
  }

  function resetPhotoInput() {
    if (photoInput.current) photoInput.current.value = '';
  }

  /**
   * Resetting the input's value is the whole point: clearing state alone leaves
   * the input holding the file, and re-picking the SAME file then fires no
   * change event, so the photo can never come back.
   */
  function clearPhoto() {
    setFile(null);
    setShrunk(null);
    setError(null);
    resetPhotoInput();
  }

  /**
   * Ward only means anything for DC, so leaving it away from DC lets a stale DC
   * ward ride along on, say, a Virginia submission. Clear the ward whenever the
   * state moves off DC.
   */
  function onStateChange(next: string) {
    setState(next);
    if (next !== DEFAULT_STATE) {
      setWard('');
    }
  }

  /**
   * State and ward only mean anything for the US. When the country moves off the
   * US, clear the state and ward so a stale US selection cannot ride along on a
   * non-US submission. Moving back to the US re-defaults the state to DC for
   * convenience, matching where a US submitter usually starts.
   */
  function onCountryChange(next: string) {
    setCountry(next);
    if (next === DEFAULT_COUNTRY) {
      setState(DEFAULT_STATE);
    } else {
      setState('');
      setWard('');
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Add a name. A first name or a handle is fine.');
      return;
    }

    try {
      let photoKey: string | undefined;
      if (file) {
        setPhase('uploading');
        photoKey = await uploadPhoto(file);
      }
      setPhase('submitting');
      await createSubmission({
        name: trimmed,
        note: note.trim() || undefined,
        email: email.trim() || undefined,
        country,
        // State and ward are US-only, so never let them out the door for another
        // country; ward is DC-only on top of that.
        state: country === DEFAULT_COUNTRY ? state : undefined,
        ward:
          country === DEFAULT_COUNTRY && state === DEFAULT_STATE
            ? ward || undefined
            : undefined,
        photoKey,
        public: isPublic,
        // Source attribution, read at submit time from the ref captured at boot
        // (see lib/ref.ts). Never shown to or editable by the submitter, and
        // undefined for anyone who did not arrive through a reference link, so
        // the field is simply absent rather than a made-up "direct".
        ref: storedRef(safeLocalStorage()),
      });
      setPhase('done');
    } catch (err) {
      setPhase('idle');
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    }
  }

  if (phase === 'done') {
    const links = shareLinks();
    return (
      <section className="section section--alt" id="share" ref={ref}>
        <div className="section__inner section__inner--narrow">
          <div className="submitted" role="status">
            <h2 className="section__title">Got it. Thank you.</h2>
            <p>
              Your submission is in the queue. Everything on the wall is reviewed
              by a person before it goes up, so give it a day or so to appear.
            </p>
            <p className="submitted__lede">Tell people you did it:</p>
            <div className="share-links">
              <a
                className="btn btn--ghost btn--small"
                href={links.bluesky}
                target="_blank"
                rel="noreferrer noopener"
              >
                Share to Bluesky
              </a>
              <a
                className="btn btn--ghost btn--small"
                href={links.twitter}
                target="_blank"
                rel="noreferrer noopener"
              >
                Share to Twitter
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="section section--alt"
      id="share"
      ref={ref}
      aria-labelledby="share-heading"
    >
      <div className="section__inner section__inner--narrow">
        <h2 id="share-heading" className="section__title">
          Tell us you deleted DoorDash
        </h2>
        <p className="section__lede">
          Deleted it? Add your name, and a photo of the moment if you took one.
          Submissions are reviewed before they appear on the wall.
        </p>

        <form className="form" onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="name">Name or handle</label>
            <input
              id="name"
              type="text"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              autoComplete="nickname"
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="email">
              Email{' '}
              <span className="field__optional">
                Enter your email to subscribe to Free DC updates
              </span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              maxLength={MAX_EMAIL_LENGTH}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="field">
            <label htmlFor="note">
              A line about why <span className="field__optional">optional</span>
            </label>
            <textarea
              id="note"
              value={note}
              rows={3}
              maxLength={MAX_NOTE_LENGTH}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
            <p className="field__count">
              {note.length}/{MAX_NOTE_LENGTH}
            </p>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="country">Country</label>
              <select
                id="country"
                value={country}
                onChange={(e) => onCountryChange(e.target.value)}
                disabled={busy}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {country === DEFAULT_COUNTRY && (
              <div className="field">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  value={state}
                  onChange={(e) => onStateChange(e.target.value)}
                  disabled={busy}
                >
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {country === DEFAULT_COUNTRY && state === DEFAULT_STATE && (
          <div className="field">
            <label htmlFor="ward">
              Ward <span className="field__optional">optional, DC only</span>
            </label>
            <select
              id="ward"
              value={ward}
              onChange={(e) => setWard(e.target.value)}
              disabled={busy}
            >
              <option value="">Select your ward</option>
              {DC_WARDS.map((w) => (
                <option key={w} value={w}>
                  Ward {w}
                </option>
              ))}
            </select>

            <p className="ward-lookup__prompt">
              Not sure which ward you&rsquo;re in? Look it up at{' '}
              <a
                href="https://planning.dc.gov/whatsmyward"
                target="_blank"
                rel="noreferrer noopener"
              >
                planning.dc.gov/whatsmyward
              </a>
            </p>
          </div>
          )}

          <div className="field">
            <label htmlFor="photo">
              Photo{' '}
              <span className="field__optional">
                optional; big ones are shrunk to fit
              </span>
            </label>
            <input
              id="photo"
              ref={photoInput}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            {phase === 'compressing' && (
              <p className="form__note" role="status">
                Shrinking that photo so it fits…
              </p>
            )}
            {previewUrl && (
              <div className="form__photo">
                <img className="form__preview" src={previewUrl} alt="Your selected photo" />
                {shrunk && (
                  <p className="form__note">
                    Shrunk from {formatBytes(shrunk.from)} to {formatBytes(shrunk.to)} so it
                    fits the {MAX_PHOTO_BYTES / 1024 / 1024}MB limit.
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={clearPhoto}
                  disabled={busy}
                >
                  Remove photo
                </button>
              </div>
            )}
          </div>

          <div className="field field--check">
            <label htmlFor="public" className="check">
              <input
                id="public"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={busy}
              />
              <span>Display my name, photo, and reason publicly.</span>
            </label>
          </div>

          {error && (
            <p className="form__error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy}
          >
            {phase === 'compressing'
              ? 'Preparing photo…'
              : phase === 'uploading'
                ? 'Uploading photo…'
                : phase === 'submitting'
                  ? 'Sending…'
                  : 'Submit'}
          </button>

          <p className="form__fineprint">
            Email is optional, used solely for campaign updates, and never
            shown publicly.
          </p>
        </form>
      </div>
    </section>
  );
});
